import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import {
  ADD_SUGGESTED_REVIEWER,
  DELETE_SUGGESTED_REVIEWER,
  ErrorResponse,
  GET_ALL_USERS_BY_FILTER,
  GET_ME,
  LIST_SUGGESTED_REVIEWERS,
  SuggestedReviewer,
  User,
} from '@frontend/data-access';
import { UserAvatar } from '@frontend/shared/ui';
import {
  App,
  Avatar,
  Button,
  Form,
  Input,
  List,
  Modal,
  Select,
  Spin,
  Typography,
} from 'antd';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { FC, useEffect, useMemo, useState } from 'react';

const WAIT_INTERVAL = 500;
const POINTS = 100;

const { Text } = Typography;

interface FormValues {
  email: string;
  description: string;
}

interface ReviewerSuggestionModalProps {
  visible: boolean;
  onClose: () => void;
}

const ReviewerSuggestionModal: FC<ReviewerSuggestionModalProps> = ({
  visible,
  onClose,
}) => {
  const { notification } = App.useApp();
  const { t: tcommon } = useTranslation('common');
  const [form] = Form.useForm<FormValues>();
  const router = useRouter();
  const id = router.query['id'] as string;
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [deleteLoadingId, setDeleteLoadingId] = useState<string | null>(null);
  const [customEmail, setCustomEmail] = useState<string>('');

  const { data: meData } = useQuery<{ me: User }>(GET_ME);
  const [searchUser, { data: userData, loading: userLoading }] = useLazyQuery<{
    publicUsers: User[];
  }>(GET_ALL_USERS_BY_FILTER);

  const [
    getReviewers,
    {
      data: reviewersData,
      refetch: refetchReviewers,
      loading: reviewersLoading,
    },
  ] = useLazyQuery<{ listSuggestedReviewers: SuggestedReviewer[] }>(
    LIST_SUGGESTED_REVIEWERS
  );

  const handleError = (error: ErrorResponse) => {
    notification.error({
      message: tcommon('error'),
      description: error.message,
      icon: (
        <span className="text-red-600">
          <CloseCircleOutlined />
        </span>
      ),
    });
  };

  const [addReviewer, { loading: addLoading }] = useMutation(
    ADD_SUGGESTED_REVIEWER,
    {
      onCompleted() {
        form.resetFields();
        notification.success({
          message: tcommon('idea_detail_suggest_reviewer_success'),
          icon: (
            <span className="text-green-600">
              <CheckCircleOutlined />
            </span>
          ),
        });
        refetchReviewers();
      },
      onError: handleError,
    }
  );

  const [deleteReviewer] = useMutation(DELETE_SUGGESTED_REVIEWER, {
    onCompleted() {
      notification.success({
        message: tcommon('idea_detail_suggest_reviewer_delete_success'),
        icon: (
          <span className="text-green-600">
            <CheckCircleOutlined />
          </span>
        ),
      });
      refetchReviewers();
    },
    onError: handleError,
  });

  const mySuggestedReviewers = useMemo(
    () =>
      reviewersData?.listSuggestedReviewers?.filter(
        (reviewer) => reviewer.suggestedBy === meData?.me?.id
      ),
    [reviewersData, meData]
  );

  const handleDeleteReviewer = async (id: string) => {
    setDeleteLoadingId(id);

    try {
      await deleteReviewer({ variables: { id } });
    } catch (error) {
      // Error handled by onError callback
    } finally {
      setDeleteLoadingId(null);
    }
  };

  const handleSelect = (value: string, option: { key: string }) => {
    form.setFieldsValue({ email: value });
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value.trim());
    setCustomEmail(value.trim());
  };

  const handleFinish = async (values: FormValues) => {
    try {
      await addReviewer({
        variables: {
          input: {
            ideaId: id,
            email: values.email,
            description: values.description,
          },
        },
      });
    } catch (error) {
      // Error handled by onError callback
    }
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchTerm) {
        searchUser({
          variables: {
            searchBy: searchTerm,
            filterBy: {
              roles: ['ADMIN', 'INTRAPRENEUR', 'CHALLENGE_MANAGER'],
              field: 'email',
            },
            limit: 100,
          },
        });
      }
    }, WAIT_INTERVAL);

    return () => clearTimeout(delayDebounceFn);
  }, [searchUser, searchTerm]);

  useEffect(() => {
    if (id) {
      getReviewers({
        variables: {
          ideaId: id,
        },
      });
    }
  }, [getReviewers, id]);

  return (
    <Modal
      title={tcommon('idea_detail_suggest_reviewer_title')}
      open={visible}
      onCancel={onClose}
      footer={null}
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark="optional"
        onFinish={handleFinish}
      >
        <Form.Item
          name="email"
          label={tcommon('idea_detail_suggest_reviewer_email_label')}
          tooltip={tcommon('idea_detail_suggest_reviewer_email_tooltip')}
          rules={[
            {
              required: true,
              type: 'email',
              message: tcommon('idea_detail_suggest_reviewer_error'),
            },
          ]}
        >
          <Select
            showSearch
            allowClear
            placeholder={tcommon(
              'idea_detail_suggest_reviewer_email_placeholder'
            )}
            loading={userLoading}
            notFoundContent={
              userLoading ? (
                <div className="text-center">
                  <Spin size="small" />
                </div>
              ) : null
            }
            filterOption={false}
            onSearch={handleSearch}
            onSelect={handleSelect}
            data-test="idea-suggest-reviewer-email-input"
          >
            {userData?.publicUsers.map((item) => (
              <Select.Option key={item.id} value={item.email}>
                <div className="flex items-center gap-2">
                  <UserAvatar size={24} user={item} />
                  {item.email}
                </div>
              </Select.Option>
            ))}
            {customEmail &&
              /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customEmail) &&
              !userData?.publicUsers.find(
                (user) =>
                  user.email?.toLowerCase() === customEmail.toLowerCase()
              ) && (
                <Select.Option key="custom" value={customEmail}>
                  {customEmail}
                </Select.Option>
              )}
          </Select>
        </Form.Item>

        <Form.Item
          name="description"
          label={tcommon('idea_detail_suggest_reviewer_description_label')}
        >
          <Input.TextArea
            placeholder={tcommon(
              'idea_detail_suggest_reviewer_description_placeholder'
            )}
            rows={4}
            data-test="idea-suggest-reviewer-description"
          />
        </Form.Item>

        <Form.Item>
          <Button
            type="primary"
            shape="round"
            htmlType="submit"
            loading={addLoading}
            disabled={mySuggestedReviewers && mySuggestedReviewers?.length >= 3}
            data-test="idea-suggest-reviewer-add"
          >
            {tcommon('idea_detail_suggest_reviewer_add')}
          </Button>
        </Form.Item>
      </Form>

      <div>
        <div className="flex justify-between mb-2">
          <h3 className="font-semibold">
            {tcommon('idea_detail_suggest_reviewer_list_title')}
          </h3>
          <p className="text-gray-600 font-normal">
            {tcommon('idea_detail_suggest_reviewer_list_max', {
              count: mySuggestedReviewers?.length || 0,
            })}
          </p>
        </div>
        <List
          itemLayout="horizontal"
          data-test="idea-suggest-reviewer-list"
          locale={{
            emptyText: (
              <div className="text-center">
                <p className="text-gray-600 font-normal">
                  {tcommon('idea_detail_suggest_reviewer_list_empty')}
                </p>
              </div>
            ),
          }}
          split={false}
          loading={reviewersLoading}
          dataSource={mySuggestedReviewers}
          renderItem={(reviewer) => (
            <List.Item className="flex justify-between items-center !px-3 !py-2 bg-gray-300 rounded-md mb-2 gap-2">
              <div className="space-y-1">
                <div
                  className="flex items-center gap-2 text-gray-900"
                  data-test="idea-suggest-reviewer-email"
                >
                  <Avatar size={24}>
                    <span className="text-sm">
                      {reviewer.email.charAt(0).toUpperCase()}
                    </span>
                  </Avatar>
                  {reviewer.email}
                </div>
                {reviewer.description && (
                  <Text className="!text-gray-700 !text-sm">
                    {tcommon('idea_detail_suggest_reviewer_note', {
                      note: reviewer.description,
                    })}
                  </Text>
                )}
              </div>
              <Button
                shape="circle"
                icon={<DeleteOutlined />}
                onClick={() => handleDeleteReviewer(reviewer.id)}
                loading={deleteLoadingId === reviewer.id}
                data-test="idea-suggest-reviewer-delete"
              />
            </List.Item>
          )}
        />
      </div>
    </Modal>
  );
};

export default ReviewerSuggestionModal;
